import { useId, useState } from "react"
import {
  BOOK_CAREADVOCATE_CALLBACK_SLOT_ACTION,
  RESCHEDULE_CAREADVOCATE_CALLBACK_SLOT_ACTION,
  type CareAdvocateCallbackSlot,
  type CareAdvocateCallbackSlotPickerProps,
} from "@/types"
import { SlotDayAccordion } from "./SlotDayAccordion"

/**
 * Care Advocate callback slot picker. Container component: owns selection
 * state and the widget action; rendering is delegated to a presentational
 * subcomponent (SlotDayAccordion today) so the presentation can change without
 * touching the action contract or the backend.
 *
 * A tap emits the action matching the member's situation — booking a first call
 * or moving one they already hold — with the slot's display label and its
 * slotStartUtc booking key, plus the current call's label when moving one; the
 * chat layer turns that into the message. Further
 * taps lock until the conversation moves on, so a retry after a failure goes
 * through the refreshed list the assistant offers.
 *
 * Rescheduling shows the call the member currently holds above the grid, since a
 * new time only means something measured against the one it replaces.
 */
export function CareAdvocateCallbackSlotPicker({ data, onAction }: CareAdvocateCallbackSlotPickerProps) {
  const [selectedSlotUtc, setSelectedSlotUtc] = useState<string | null>(null)
  const headingId = useId()

  const days = data.days.filter((day) => day.slots.length > 0)
  if (days.length === 0) {
    return null
  }

  const isRescheduling = data.mode === "reschedule"

  const handleSelect = (dayLabel: string, slot: CareAdvocateCallbackSlot) => {
    if (selectedSlotUtc !== null) return
    setSelectedSlotUtc(slot.slotStartUtc)
    const label = `${dayLabel} at ${slot.localLabel}`
    if (isRescheduling) {
      onAction?.(RESCHEDULE_CAREADVOCATE_CALLBACK_SLOT_ACTION, {
        label,
        slotStartUtc: slot.slotStartUtc,
        currentBookingLabel: data.currentBookingLabel ?? null,
      })
      return
    }
    onAction?.(BOOK_CAREADVOCATE_CALLBACK_SLOT_ACTION, {
      label,
      slotStartUtc: slot.slotStartUtc,
    })
  }

  return (
    <section
      className="w-full rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
      aria-labelledby={headingId}
    >
      <h3 id={headingId} className="text-sm font-semibold text-gray-900">
        {isRescheduling
          ? "Pick a new time for your Care Advocate call"
          : "Pick a time for your Care Advocate call"}
      </h3>
      {isRescheduling && data.currentBookingLabel && (
        <p className="mt-1 text-xs text-gray-500">
          Your call is currently {data.currentBookingLabel}.
        </p>
      )}
      {data.timezoneNote && (
        <p className="mt-1 text-xs text-gray-500">{data.timezoneNote}</p>
      )}
      <div className="mt-2">
        <SlotDayAccordion
          days={days}
          selectedSlotUtc={selectedSlotUtc}
          onSelect={handleSelect}
        />
      </div>
    </section>
  )
}
